import React, { useEffect, useState, useRef } from 'react';
import { ChevronDownIcon, ChevronRightIcon, UsersIcon, CalendarIcon, PaletteIcon, AnchorIcon, PencilIcon, ShieldIcon, PlaneIcon, FileTextIcon, PhoneIcon, SearchIcon, StarIcon } from 'lucide-react';
export function MegaNavigation() {
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [activeRegion, setActiveRegion] = useState('Europe');
  const [activeSubRegion, setActiveSubRegion] = useState('All');
  const navRef = useRef<HTMLDivElement>(null);
  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        setActiveMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  const toggleMenu = (menu: string) => {
    setActiveMenu(activeMenu === menu ? null : menu);
  };
  const handleRegionClick = (region: string) => {
    setActiveRegion(region);
    setActiveSubRegion('All');
  };
  const handleSubRegionClick = (subRegion: string) => {
    setActiveSubRegion(subRegion);
  };
  // Define regions and their sub-regions
  const regions = [{
    name: 'All Destinations',
    subRegions: ['All']
  }, {
    name: 'Africa',
    subRegions: ['All', 'North Africa', 'East Africa', 'West Africa', 'Southern Africa']
  }, {
    name: 'Antarctica',
    subRegions: ['All']
  }, {
    name: 'Asia',
    subRegions: ['All', 'East Asia', 'Southeast Asia', 'South Asia', 'Central Asia']
  }, {
    name: 'China',
    subRegions: ['All', 'North China', 'South China', 'East China', 'West China']
  }, {
    name: 'Europe',
    subRegions: ['All', 'Central Europe', 'Arctic', 'Eastern Europe', 'Northern Europe', 'Southern Europe', 'Exotic']
  }, {
    name: 'Middle East',
    subRegions: ['All', 'Gulf States', 'Levant', 'Arabian Peninsula']
  }, {
    name: 'North & South America',
    subRegions: ['All', 'North America', 'Central America', 'Caribbean', 'South America']
  }];
  // Define countries by region
  const countriesByRegion: Record<string, string[]> = {
    Europe: ['Austria', 'Greece', 'France', 'Spain', 'Belgium', 'Hungary', 'Germany', 'Sweden', 'Bosnia & Herzegovina', 'Iceland', 'Netherlands', 'Turkiye', 'Croatia', 'Ireland', 'Norway', 'Vatican City', 'Czech Republic', 'Italy', 'Poland', 'Denmark', 'Liechtenstein', 'Portugal', 'Finland', 'Malta', 'Slovakia', 'Switzerland', 'Montenegro', 'Slovenia'],
    Asia: ['Japan', 'Thailand', 'Vietnam', 'Indonesia', 'South Korea', 'Malaysia', 'Philippines', 'Singapore', 'India', 'Sri Lanka', 'Nepal', 'Bhutan', 'Mongolia', 'Taiwan', 'Cambodia', 'Laos', 'Myanmar', 'Bangladesh', 'Maldives', 'Brunei'],
    Africa: ['South Africa', 'Morocco', 'Egypt', 'Kenya', 'Tanzania', 'Ethiopia', 'Namibia', 'Botswana', 'Zimbabwe', 'Uganda', 'Rwanda', 'Seychelles', 'Mauritius', 'Ghana', 'Senegal', 'Madagascar'],
    Antarctica: ['Antarctica'],
    China: ['Beijing', 'Shanghai', 'Guangzhou', 'Shenzhen', 'Chengdu', "Xi'an", 'Hangzhou', 'Nanjing', 'Chongqing', 'Guilin', 'Suzhou', 'Xiamen', 'Kunming', 'Harbin', 'Lhasa', 'Hong Kong'],
    'Middle East': ['United Arab Emirates', 'Qatar', 'Saudi Arabia', 'Oman', 'Jordan', 'Israel', 'Lebanon', 'Bahrain', 'Kuwait', 'Egypt', 'Turkey', 'Iran'],
    'North & South America': ['United States', 'Canada', 'Mexico', 'Brazil', 'Argentina', 'Peru', 'Chile', 'Colombia', 'Costa Rica', 'Cuba', 'Jamaica', 'Panama', 'Ecuador', 'Bolivia', 'Uruguay', 'Paraguay'],
    'All Destinations': ['Switzerland', 'Japan', 'United States', 'Australia', 'Italy', 'France', 'Spain', 'Thailand', 'Greece', 'Canada', 'United Kingdom', 'Germany', 'Singapore', 'New Zealand', 'South Africa', 'Brazil']
  };
  return <div className="bg-white shadow-sm" ref={navRef} data-id="element-728">
      <div className="container mx-auto px-4" data-id="element-729">
        <div className="flex items-center justify-between h-16" data-id="element-730">
          <nav className="hidden lg:flex space-x-1" data-id="element-731">
            {/* Plan Your Journey Dropdown */}
            <div className="relative" data-id="element-732">
              <button className={`px-4 py-2 text-gray-700 hover:text-blue-600 font-medium flex items-center ${activeMenu === 'journey' ? 'text-blue-600' : ''}`} onClick={() => toggleMenu('journey')} aria-expanded={activeMenu === 'journey'} data-id="element-733">
                <span data-id="element-734">Plan Your Journey</span>
                <ChevronDownIcon size={16} className={`ml-1 transition-transform ${activeMenu === 'journey' ? 'rotate-180' : ''}`} data-id="element-735" />
              </button>
              {activeMenu === 'journey' && <div className="absolute left-0 mt-2 w-[1200px] max-w-screen-xl bg-white shadow-lg rounded-lg overflow-hidden z-50 border border-gray-200 transition-all duration-300 ease-in-out" data-id="element-736">
                  <div className="flex" data-id="element-737">
                    {/* Left Column: Trip Types */}
                    <div className="w-[300px] bg-gray-50 p-6" data-id="element-738">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-739">
                        Trip Types
                      </h3>
                      <ul className="space-y-4" data-id="element-740">
                        <li data-id="element-741">
                          <a href="#" className="flex items-start group" data-id="element-742">
                            <div className="mr-3 p-2 bg-blue-100 rounded-full text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-colors" data-id="element-743">
                              <UsersIcon size={20} data-id="element-744" />
                            </div>
                            <div data-id="element-745">
                              <div className="font-medium text-gray-900 group-hover:text-blue-600" data-id="element-746">
                                Group Holidays
                                <span className="ml-2 px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full" data-id="element-747">
                                  Most Popular
                                </span>
                              </div>
                              <p className="text-sm text-gray-600" data-id="element-748">
                                Travel with like-minded explorers
                              </p>
                            </div>
                          </a>
                        </li>
                        <li data-id="element-749">
                          <a href="#" className="flex items-start group" data-id="element-750">
                            <div className="mr-3 p-2 bg-purple-100 rounded-full text-purple-600 group-hover:bg-purple-600 group-hover:text-white transition-colors" data-id="element-751">
                              <CalendarIcon size={20} data-id="element-752" />
                            </div>
                            <div data-id="element-753">
                              <div className="font-medium text-gray-900 group-hover:text-purple-600" data-id="element-754">
                                Flexi Holidays
                              </div>
                              <p className="text-sm text-gray-600" data-id="element-755">
                                Travel at your own pace and schedule
                              </p>
                            </div>
                          </a>
                        </li>
                        <li data-id="element-756">
                          <a href="#" className="flex items-start group" data-id="element-757">
                            <div className="mr-3 p-2 bg-green-100 rounded-full text-green-600 group-hover:bg-green-600 group-hover:text-white transition-colors" data-id="element-758">
                              <PaletteIcon size={20} data-id="element-759" />
                            </div>
                            <div data-id="element-760">
                              <div className="font-medium text-gray-900 group-hover:text-green-600" data-id="element-761">
                                Themed Journeys
                              </div>
                              <p className="text-sm text-gray-600" data-id="element-762">
                                Food, culture, photography and more
                              </p>
                            </div>
                          </a>
                        </li>
                        <li data-id="element-763">
                          <a href="#" className="flex items-start group" data-id="element-764">
                            <div className="mr-3 p-2 bg-cyan-100 rounded-full text-cyan-600 group-hover:bg-cyan-600 group-hover:text-white transition-colors" data-id="element-765">
                              <AnchorIcon size={20} data-id="element-766" />
                            </div>
                            <div data-id="element-767">
                              <div className="font-medium text-gray-900 group-hover:text-cyan-600" data-id="element-768">
                                Cruise Holidays
                              </div>
                              <p className="text-sm text-gray-600" data-id="element-769">
                                Explore the world's waterways
                              </p>
                            </div>
                          </a>
                        </li>
                        <li data-id="element-770">
                          <a href="#" className="flex items-start group" data-id="element-771">
                            <div className="mr-3 p-2 bg-amber-100 rounded-full text-amber-600 group-hover:bg-amber-600 group-hover:text-white transition-colors" data-id="element-772">
                              <PencilIcon size={20} data-id="element-773" />
                            </div>
                            <div data-id="element-774">
                              <div className="font-medium text-gray-900 group-hover:text-amber-600" data-id="element-775">
                                Custom Itineraries
                              </div>
                              <p className="text-sm text-gray-600" data-id="element-776">
                                Tailored to your preferences
                              </p>
                            </div>
                          </a>
                        </li>
                      </ul>
                    </div>
                    {/* Right Column: Quick Links */}
                    <div className="flex-1 p-6" data-id="element-777">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-778">
                        Quick Links
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-id="element-779">
                        <a href="#" className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-blue-200 transition-colors" data-id="element-780">
                          <div className="flex items-center mb-2" data-id="element-781">
                            <div className="p-2 bg-blue-100 rounded-full text-blue-600 mr-3" data-id="element-782">
                              <StarIcon size={18} data-id="element-783" />
                            </div>
                            <h4 className="font-medium text-gray-900" data-id="element-784">
                              Why Travel With Us
                            </h4>
                          </div>
                          <p className="text-sm text-gray-600" data-id="element-785">
                            Discover what makes our tours special
                          </p>
                        </a>
                        <a href="#" className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-blue-200 transition-colors" data-id="element-786">
                          <div className="flex items-center mb-2" data-id="element-787">
                            <div className="p-2 bg-blue-100 rounded-full text-blue-600 mr-3" data-id="element-788">
                              <ShieldIcon size={18} data-id="element-789" />
                            </div>
                            <h4 className="font-medium text-gray-900" data-id="element-790">
                              Travel Insurance
                            </h4>
                          </div>
                          <p className="text-sm text-gray-600" data-id="element-791">
                            Protect your journey with comprehensive coverage
                          </p>
                        </a>
                        <a href="#" className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-blue-200 transition-colors" data-id="element-792">
                          <div className="flex items-center mb-2" data-id="element-793">
                            <div className="p-2 bg-blue-100 rounded-full text-blue-600 mr-3" data-id="element-794">
                              <FileTextIcon size={18} data-id="element-795" />
                            </div>
                            <h4 className="font-medium text-gray-900" data-id="element-796">
                              Visa Services
                            </h4>
                          </div>
                          <p className="text-sm text-gray-600" data-id="element-797">
                            Hassle-free visa application assistance
                          </p>
                        </a>
                        <a href="#" className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-blue-200 transition-colors" data-id="element-798">
                          <div className="flex items-center mb-2" data-id="element-799">
                            <div className="p-2 bg-blue-100 rounded-full text-blue-600 mr-3" data-id="element-800">
                              <PlaneIcon size={18} data-id="element-801" />
                            </div>
                            <h4 className="font-medium text-gray-900" data-id="element-802">
                              Travel Guidelines
                            </h4>
                          </div>
                          <p className="text-sm text-gray-600" data-id="element-803">
                            Essential information for a smooth journey
                          </p>
                        </a>
                        <a href="#" className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-blue-200 transition-colors" data-id="element-804">
                          <div className="flex items-center mb-2" data-id="element-805">
                            <div className="p-2 bg-blue-100 rounded-full text-blue-600 mr-3" data-id="element-806">
                              <PhoneIcon size={18} data-id="element-807" />
                            </div>
                            <h4 className="font-medium text-gray-900" data-id="element-808">
                              Book Consultation
                            </h4>
                          </div>
                          <p className="text-sm text-gray-600" data-id="element-809">
                            Speak with our travel experts
                          </p>
                        </a>
                      </div>
                      <div className="mt-8 bg-blue-50 p-4 rounded-lg" data-id="element-810">
                        <h4 className="font-medium text-gray-900 mb-2" data-id="element-811">
                          Need Help Planning?
                        </h4>
                        <p className="text-sm text-gray-700 mb-3" data-id="element-812">
                          Our travel experts are ready to create your perfect
                          journey.
                        </p>
                        <div className="flex" data-id="element-813">
                          <a href="#" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium" data-id="element-814">
                            Schedule a Call
                          </a>
                          <a href="#" className="ml-3 px-4 py-2 border border-blue-600 text-blue-600 rounded-md hover:bg-blue-50 transition-colors text-sm font-medium" data-id="element-815">
                            View Sample Itineraries
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>}
            </div>
            {/* Trending Destinations Dropdown */}
            <div className="relative" data-id="element-816">
              <button className={`px-4 py-2 text-gray-700 hover:text-blue-600 font-medium flex items-center ${activeMenu === 'destinations' ? 'text-blue-600' : ''}`} onClick={() => toggleMenu('destinations')} aria-expanded={activeMenu === 'destinations'} data-id="element-817">
                <span data-id="element-818">Trending Destinations</span>
                <ChevronDownIcon size={16} className={`ml-1 transition-transform ${activeMenu === 'destinations' ? 'rotate-180' : ''}`} data-id="element-819" />
              </button>
              {activeMenu === 'destinations' && <div className="absolute left-0 mt-2 w-[1200px] max-w-screen-xl bg-white shadow-lg rounded-lg overflow-hidden z-50 border border-gray-200 transition-all duration-300 ease-in-out" data-id="element-820">
                  <div className="flex" data-id="element-821">
                    {/* Left Column: Regions */}
                    <div className="w-[250px] bg-gray-50 py-4" data-id="element-822">
                      <div className="px-4 pb-2 mb-2 border-b border-gray-200" data-id="element-823">
                        <div className="relative" data-id="element-824">
                          <input type="text" placeholder="Search destinations..." className="w-full pl-8 pr-4 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-825" />
                          <SearchIcon size={16} className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-826" />
                        </div>
                      </div>
                      <ul data-id="element-827">
                        {regions.map(region => <li key={region.name} data-id="element-828">
                            <button className={`w-full text-left px-4 py-3 flex items-center justify-between ${activeRegion === region.name ? 'bg-blue-50 text-blue-600 font-medium' : 'text-gray-700 hover:bg-gray-100'}`} onClick={() => handleRegionClick(region.name)} data-id="element-829">
                              <span data-id="element-830">{region.name}</span>
                              <ChevronRightIcon size={16} className={activeRegion === region.name ? 'text-blue-600' : 'text-gray-400'} data-id="element-831" />
                            </button>
                          </li>)}
                      </ul>
                    </div>
                    {/* Middle Column: Sub-regions */}
                    <div className="w-[300px] border-l border-r border-gray-200 py-4" data-id="element-832">
                      <h3 className="px-4 text-sm font-medium text-gray-500 mb-2" data-id="element-833">
                        CHOOSE REGION
                      </h3>
                      <ul data-id="element-834">
                        {regions.find(r => r.name === activeRegion)?.subRegions.map(subRegion => <li key={subRegion} data-id="element-835">
                              <button className={`w-full text-left px-4 py-3 ${activeSubRegion === subRegion ? 'bg-blue-50 text-blue-600 font-medium' : 'text-gray-700 hover:bg-gray-100'}`} onClick={() => handleSubRegionClick(subRegion)} data-id="element-836">
                                {subRegion}
                              </button>
                            </li>)}
                      </ul>
                    </div>
                    {/* Right Column: Countries */}
                    <div className="flex-1 p-6" data-id="element-837">
                      <h3 className="text-sm font-medium text-gray-500 mb-4" data-id="element-838">
                        COUNTRIES (
                        {countriesByRegion[activeRegion]?.length || 0})
                      </h3>
                      <div className="grid grid-cols-4 gap-2" data-id="element-839">
                        {countriesByRegion[activeRegion]?.map(country => <a key={country} href={`/country/${country.toLowerCase().replace(/\s+/g, '-')}`} className={`py-2 px-3 rounded hover:bg-gray-50 text-sm ${country === 'Switzerland' ? 'bg-blue-50 text-blue-600 font-medium' : 'text-gray-700'}`} data-id="element-840">
                            {country}
                          </a>)}
                      </div>
                      <div className="mt-8" data-id="element-841">
                        <h3 className="text-sm font-medium text-gray-500 mb-4" data-id="element-842">
                          POPULAR DESTINATIONS
                        </h3>
                        <div className="grid grid-cols-3 gap-4" data-id="element-843">
                          <a href="#" className="group block rounded-lg overflow-hidden" data-id="element-844">
                            <div className="relative aspect-[4/3]" data-id="element-845">
                              <img src="https://images.unsplash.com/photo-1527668752968-14dc70a27c95?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=300&q=80" alt="Switzerland" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" data-id="element-846" />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" data-id="element-847"></div>
                              <div className="absolute bottom-0 left-0 right-0 p-3 text-white" data-id="element-848">
                                <div className="font-medium" data-id="element-849">Switzerland</div>
                                <div className="text-xs" data-id="element-850">
                                  24 tours available
                                </div>
                              </div>
                            </div>
                          </a>
                          <a href="#" className="group block rounded-lg overflow-hidden" data-id="element-851">
                            <div className="relative aspect-[4/3]" data-id="element-852">
                              <img src="https://images.unsplash.com/photo-1499678329028-101435549a4e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=300&q=80" alt="Italy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" data-id="element-853" />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" data-id="element-854"></div>
                              <div className="absolute bottom-0 left-0 right-0 p-3 text-white" data-id="element-855">
                                <div className="font-medium" data-id="element-856">Italy</div>
                                <div className="text-xs" data-id="element-857">
                                  18 tours available
                                </div>
                              </div>
                            </div>
                          </a>
                          <a href="#" className="group block rounded-lg overflow-hidden" data-id="element-858">
                            <div className="relative aspect-[4/3]" data-id="element-859">
                              <img src="https://images.unsplash.com/photo-1503917988258-f87a78e3c995?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=300&q=80" alt="France" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" data-id="element-860" />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" data-id="element-861"></div>
                              <div className="absolute bottom-0 left-0 right-0 p-3 text-white" data-id="element-862">
                                <div className="font-medium" data-id="element-863">France</div>
                                <div className="text-xs" data-id="element-864">
                                  16 tours available
                                </div>
                              </div>
                            </div>
                          </a>
                        </div>
                      </div>
                      <div className="mt-6 flex justify-center" data-id="element-865">
                        <a href="/destinations" className="text-blue-600 hover:text-blue-800 font-medium flex items-center" data-id="element-866">
                          View All Destinations
                          <ChevronRightIcon size={16} className="ml-1" data-id="element-867" />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>}
            </div>
            {/* Deals Dropdown */}
            <div className="relative" data-id="element-868">
              <button className={`px-4 py-2 text-gray-700 hover:text-blue-600 font-medium flex items-center ${activeMenu === 'deals' ? 'text-blue-600' : ''}`} onClick={() => toggleMenu('deals')} aria-expanded={activeMenu === 'deals'} data-id="element-869">
                <span data-id="element-870">Deals</span>
                <ChevronDownIcon size={16} className={`ml-1 transition-transform ${activeMenu === 'deals' ? 'rotate-180' : ''}`} data-id="element-871" />
              </button>
              {activeMenu === 'deals' && <div className="absolute left-0 mt-2 w-64 bg-white shadow-lg rounded-lg overflow-hidden z-50 border border-gray-200" data-id="element-872">
                  <ul className="py-2" data-id="element-873">
                    <li data-id="element-874">
                      <a href="#" className="block px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600" data-id="element-875">
                        Early Bird Offers
                      </a>
                    </li>
                    <li data-id="element-876">
                      <a href="#" className="block px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600" data-id="element-877">
                        Last Minute Deals
                      </a>
                    </li>
                    <li data-id="element-878">
                      <a href="#" className="block px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600" data-id="element-879">
                        Group Discounts
                      </a>
                    </li>
                    <li data-id="element-880">
                      <a href="#" className="block px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600" data-id="element-881">
                        Seasonal Specials
                      </a>
                    </li>
                    <li data-id="element-882">
                      <a href="#" className="block px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600" data-id="element-883">
                        Member-Only Offers
                      </a>
                    </li>
                  </ul>
                </div>}
            </div>
            {/* About Us Dropdown */}
            <div className="relative" data-id="element-884">
              <button className={`px-4 py-2 text-gray-700 hover:text-blue-600 font-medium flex items-center ${activeMenu === 'about' ? 'text-blue-600' : ''}`} onClick={() => toggleMenu('about')} aria-expanded={activeMenu === 'about'} data-id="element-885">
                <span data-id="element-886">About Us</span>
                <ChevronDownIcon size={16} className={`ml-1 transition-transform ${activeMenu === 'about' ? 'rotate-180' : ''}`} data-id="element-887" />
              </button>
              {activeMenu === 'about' && <div className="absolute left-0 mt-2 w-64 bg-white shadow-lg rounded-lg overflow-hidden z-50 border border-gray-200" data-id="element-888">
                  <ul className="py-2" data-id="element-889">
                    <li data-id="element-890">
                      <a href="#" className="block px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600" data-id="element-891">
                        Our Story
                      </a>
                    </li>
                    <li data-id="element-892">
                      <a href="#" className="block px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600" data-id="element-893">
                        Team
                      </a>
                    </li>
                    <li data-id="element-894">
                      <a href="#" className="block px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600" data-id="element-895">
                        Testimonials
                      </a>
                    </li>
                    <li data-id="element-896">
                      <a href="#" className="block px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600" data-id="element-897">
                        Careers
                      </a>
                    </li>
                    <li data-id="element-898">
                      <a href="#" className="block px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600" data-id="element-899">
                        Press & Media
                      </a>
                    </li>
                  </ul>
                </div>}
            </div>
            {/* Our Brands Dropdown */}
            <div className="relative" data-id="element-900">
              <button className={`px-4 py-2 text-gray-700 hover:text-blue-600 font-medium flex items-center ${activeMenu === 'brands' ? 'text-blue-600' : ''}`} onClick={() => toggleMenu('brands')} aria-expanded={activeMenu === 'brands'} data-id="element-901">
                <span data-id="element-902">Our Brands</span>
                <ChevronDownIcon size={16} className={`ml-1 transition-transform ${activeMenu === 'brands' ? 'rotate-180' : ''}`} data-id="element-903" />
              </button>
              {activeMenu === 'brands' && <div className="absolute left-0 mt-2 w-64 bg-white shadow-lg rounded-lg overflow-hidden z-50 border border-gray-200" data-id="element-904">
                  <ul className="py-2" data-id="element-905">
                    <li data-id="element-906">
                      <a href="#" className="block px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600" data-id="element-907">
                        EU Holidays
                      </a>
                    </li>
                    <li data-id="element-908">
                      <a href="#" className="block px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600" data-id="element-909">
                        EU Cruise
                      </a>
                    </li>
                    <li data-id="element-910">
                      <a href="#" className="block px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600" data-id="element-911">
                        EU Asia
                      </a>
                    </li>
                    <li data-id="element-912">
                      <a href="#" className="block px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600" data-id="element-913">
                        EU Exotic
                      </a>
                    </li>
                    <li data-id="element-914">
                      <a href="#" className="block px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600" data-id="element-915">
                        Partner Agencies
                      </a>
                    </li>
                  </ul>
                </div>}
            </div>
          </nav>
          <div className="flex items-center" data-id="element-916">
            <button className="lg:hidden text-gray-700 hover:text-blue-600" data-id="element-917">
              <SearchIcon size={20} data-id="element-918" />
            </button>
          </div>
        </div>
      </div>
    </div>;
}